import { adminService } from "@/api/admin/admin.service";
import type {
  TAdminPermissionDetail,
  TAdminRole,
  TAdminUser,
  TRoleUser,
} from "@/api/admin/admin.types";
import { UserPicker } from "@/components/admin";
import { Button } from "@/components/ui/Button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Input } from "@/components/ui/Input";
import { Skeleton } from "@/components/ui/Skeleton";
import { Textarea } from "@/components/ui/Textarea";
import { useToast } from "@/components/ui/Toast";
import { useClientTableControls } from "@/hooks";
import { extractErrorMessage } from "@/utils";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  Loader2,
  Pencil,
  Plus,
  RefreshCw,
  Search,
  Shield,
  ShieldCheck,
  Trash2,
  Users,
  UserMinus,
  UserPlus,
  X,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";

type TDetailTab = "permissions" | "users";

type TRoleEditor = {
  open: boolean;
  role: TAdminRole | null;
  name: string;
  description: string;
};

const EMPTY_ROLE_EDITOR: TRoleEditor = {
  open: false,
  role: null,
  name: "",
  description: "",
};

const USERS_PAGE_SIZE = 10;
const PERMS_PAGE_SIZE = 12;

export function RolesPermissionsPage() {
  const { toast } = useToast();

  const [roles, setRoles] = useState<TAdminRole[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);
  const [rolesError, setRolesError] = useState<string | null>(null);
  const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null);

  const [allPermissions, setAllPermissions] = useState<TAdminPermissionDetail[]>([]);
  const [rolePermissions, setRolePermissions] = useState<TAdminPermissionDetail[]>([]);
  const [permsLoading, setPermsLoading] = useState(false);
  const [permMutating, setPermMutating] = useState<string | null>(null);

  const [roleUsers, setRoleUsers] = useState<TRoleUser[]>([]);
  const [roleUsersTotal, setRoleUsersTotal] = useState(0);
  const [roleUsersOffset, setRoleUsersOffset] = useState(0);
  const [usersLoading, setUsersLoading] = useState(false);
  const [assignUser, setAssignUser] = useState<TAdminUser | null>(null);
  const [userMutating, setUserMutating] = useState<string | null>(null);

  const [permPage, setPermPage] = useState(1);

  const [tab, setTab] = useState<TDetailTab>("permissions");

  const [roleEditor, setRoleEditor] = useState<TRoleEditor>(EMPTY_ROLE_EDITOR);
  const [isSavingRole, setIsSavingRole] = useState(false);
  const [deleteRoleTarget, setDeleteRoleTarget] = useState<TAdminRole | null>(null);
  const [isDeletingRole, setIsDeletingRole] = useState(false);

  const selectedRole = useMemo(
    () => roles.find((r) => r.role_id === selectedRoleId) ?? null,
    [roles, selectedRoleId]
  );

  const loadRoles = useCallback(
    async (signal?: AbortSignal) => {
      setRolesLoading(true);
      setRolesError(null);
      const { data, error: err } = await adminService.getRoles({ signal });
      if (signal?.aborted) return;
      if (err) {
        setRolesError(await extractErrorMessage(err));
      } else if (data) {
        const list = (data.data ?? []) as TAdminRole[];
        setRoles(list);
        setSelectedRoleId((prev) => prev ?? list[0]?.role_id ?? null);
      }
      setRolesLoading(false);
    },
    []
  );

  const loadAllPermissions = useCallback(async (signal?: AbortSignal) => {
    const { data } = await adminService.getPermissions({ signal });
    if (signal?.aborted) return;
    setAllPermissions(data?.data ?? []);
  }, []);

  const loadRolePermissions = useCallback(async (roleId: string, signal?: AbortSignal) => {
    setPermsLoading(true);
    const { data } = await adminService.getRolePermissions(roleId, { signal });
    if (signal?.aborted) return;
    setRolePermissions(data?.data ?? []);
    setPermsLoading(false);
  }, []);

  const loadRoleUsers = useCallback(async (roleId: string, offset: number, signal?: AbortSignal) => {
    setUsersLoading(true);
    const { data } = await adminService.getRoleUsers(
      roleId,
      { limit: USERS_PAGE_SIZE, offset },
      { signal }
    );
    if (signal?.aborted) return;
    setRoleUsers(data?.data?.items ?? []);
    setRoleUsersTotal(data?.data?.total_count ?? 0);
    setUsersLoading(false);
  }, []);

  useEffect(() => {
    const ctrl = new AbortController();
    void loadRoles(ctrl.signal);
    void loadAllPermissions(ctrl.signal);
    return () => ctrl.abort();
  }, [loadRoles, loadAllPermissions]);

  useEffect(() => {
    if (!selectedRoleId) return;
    setRoleUsersOffset(0);
    setPermPage(1);
    const ctrl = new AbortController();
    void loadRolePermissions(selectedRoleId, ctrl.signal);
    void loadRoleUsers(selectedRoleId, 0, ctrl.signal);
    return () => ctrl.abort();
  }, [selectedRoleId, loadRolePermissions, loadRoleUsers]);

  const goToUsersPage = (offset: number) => {
    if (!selectedRoleId) return;
    setRoleUsersOffset(offset);
    void loadRoleUsers(selectedRoleId, offset);
  };

  const assignedIds = useMemo(
    () => new Set(rolePermissions.map((p) => p.permission_id)),
    [rolePermissions]
  );

  // Client-side search + sort for the roles sidebar (bare list).
  const {
    rows: filteredRoles,
    search: roleSearch,
    setSearch: setRoleSearch,
  } = useClientTableControls<TAdminRole>({
    data: roles,
    searchFields: ["name", "description"],
  });

  // Client-side search + sort for the permission list (bare list).
  const {
    rows: filteredPermissions,
    search: permSearch,
    setSearch: setPermSearch,
    sortColumn: permSortColumn,
    sortDirection: permSortDirection,
  } = useClientTableControls<TAdminPermissionDetail>({
    data: allPermissions,
    searchFields: ["name", "resource", "action"],
  });

  // Reset to first page when the permission filter/sort changes.
  useEffect(() => {
    setPermPage(1);
  }, [permSearch, permSortColumn, permSortDirection]);

  // Client-side pagination over the filtered + sorted permission list.
  const permTotalPages = Math.max(1, Math.ceil(filteredPermissions.length / PERMS_PAGE_SIZE));
  const pagedPermissions = useMemo(
    () => filteredPermissions.slice((permPage - 1) * PERMS_PAGE_SIZE, permPage * PERMS_PAGE_SIZE),
    [filteredPermissions, permPage]
  );

  // Server-side pagination for role users.
  const usersTotalPages = Math.max(1, Math.ceil(roleUsersTotal / USERS_PAGE_SIZE));
  const usersCurrentPage = Math.floor(roleUsersOffset / USERS_PAGE_SIZE) + 1;

  const handleRefresh = () => {
    void loadRoles();
    void loadAllPermissions();
    if (selectedRoleId) {
      void loadRolePermissions(selectedRoleId);
      void loadRoleUsers(selectedRoleId, roleUsersOffset);
    }
  };

  const openCreateRole = () => setRoleEditor({ ...EMPTY_ROLE_EDITOR, open: true });

  const openEditRole = (role: TAdminRole) =>
    setRoleEditor({
      open: true,
      role,
      name: role.name ?? "",
      description: role.description ?? "",
    });

  const handleSaveRole = async () => {
    if (!roleEditor.name.trim()) {
      toast({ variant: "destructive", title: "Name is required" });
      return;
    }
    setIsSavingRole(true);
    if (roleEditor.role) {
      const { error: err } = await adminService.updateRole(roleEditor.role.role_id, {
        name: roleEditor.name.trim(),
        description: roleEditor.description.trim() || null,
      });
      if (err) {
        toast({
          variant: "destructive",
          title: "Failed to update role",
          description: await extractErrorMessage(err),
        });
      } else {
        toast({ title: "Role updated" });
        setRoleEditor(EMPTY_ROLE_EDITOR);
        await loadRoles();
      }
    } else {
      const { data, error: err } = await adminService.createRole({
        name: roleEditor.name.trim(),
        description: roleEditor.description.trim() || null,
      });
      if (err) {
        toast({
          variant: "destructive",
          title: "Failed to create role",
          description: await extractErrorMessage(err),
        });
      } else {
        toast({ title: "Role created" });
        setRoleEditor(EMPTY_ROLE_EDITOR);
        await loadRoles();
        if (data?.data?.role_id) setSelectedRoleId(data.data.role_id);
      }
    }
    setIsSavingRole(false);
  };

  const handleDeleteRole = async () => {
    if (!deleteRoleTarget) return;
    setIsDeletingRole(true);
    const { error: err } = await adminService.deleteRole(deleteRoleTarget.role_id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to delete role",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Role deleted" });
      if (selectedRoleId === deleteRoleTarget.role_id) setSelectedRoleId(null);
      setDeleteRoleTarget(null);
      await loadRoles();
    }
    setIsDeletingRole(false);
  };

  const togglePermission = async (permission: TAdminPermissionDetail) => {
    if (!selectedRoleId) return;
    setPermMutating(permission.permission_id);
    const isAssigned = assignedIds.has(permission.permission_id);
    const { error: err } = isAssigned
      ? await adminService.revokePermissionFromRole(selectedRoleId, permission.permission_id)
      : await adminService.assignPermissionsToRole(selectedRoleId, [permission.permission_id]);
    if (err) {
      toast({
        variant: "destructive",
        title: isAssigned ? "Failed to remove permission" : "Failed to add permission",
        description: await extractErrorMessage(err),
      });
    } else {
      await loadRolePermissions(selectedRoleId);
    }
    setPermMutating(null);
  };

  const handleAssignUser = async () => {
    if (!selectedRoleId) return;
    if (!assignUser) {
      toast({ variant: "destructive", title: "Select a user first" });
      return;
    }
    setUserMutating("assign");
    const { error: err } = await adminService.assignRoleToUser(selectedRoleId, assignUser.id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to assign role",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "Role assigned to user" });
      setAssignUser(null);
      await loadRoleUsers(selectedRoleId, roleUsersOffset);
    }
    setUserMutating(null);
  };

  const handleRemoveUser = async (user: TRoleUser) => {
    if (!selectedRoleId) return;
    setUserMutating(user.id);
    const { error: err } = await adminService.revokeRoleFromUser(selectedRoleId, user.id);
    if (err) {
      toast({
        variant: "destructive",
        title: "Failed to remove user from role",
        description: await extractErrorMessage(err),
      });
    } else {
      toast({ title: "User removed from role" });
      // If we removed the last row on a non-first page, step back a page.
      const nextOffset =
        roleUsers.length === 1 && roleUsersOffset > 0
          ? Math.max(0, roleUsersOffset - USERS_PAGE_SIZE)
          : roleUsersOffset;
      setRoleUsersOffset(nextOffset);
      await loadRoleUsers(selectedRoleId, nextOffset);
    }
    setUserMutating(null);
  };

  const userName = (u: TRoleUser) => {
    const name = `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim();
    if (name) return name;
    return u.email ?? u.id;
  };

  const userInitials = (u: TRoleUser) => {
    const first = u.first_name?.trim()?.[0] ?? "";
    const last = u.last_name?.trim()?.[0] ?? "";
    const combined = `${first}${last}`.trim();
    if (combined) return combined.toUpperCase();
    return (u.email?.trim()?.[0] ?? "?").toUpperCase();
  };

  const assignedCount = rolePermissions.length;

  return (
    <div className="h-full overflow-y-auto bg-background p-4 sm:p-6">
      <div className="mx-auto max-w-7xl space-y-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="hidden h-11 w-11 items-center justify-center rounded-xl bg-primary/10 text-primary sm:flex">
              <ShieldCheck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-foreground sm:text-3xl">
                Roles &amp; Permissions
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                Manage roles, the permissions they grant, and the users assigned to them
              </p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[300px_1fr]">
          {/* Roles list (left) */}
          <div className="flex flex-col rounded-xl border border-border bg-card">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <div className="flex items-center gap-2">
                <Shield className="h-4 w-4 text-muted-foreground" />
                <h2 className="text-sm font-semibold text-foreground">Roles</h2>
                {!rolesLoading && !rolesError && roles.length > 0 && (
                  <span className="rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                    {roles.length}
                  </span>
                )}
              </div>
              <Button size="sm" onClick={openCreateRole}>
                <Plus className="mr-1 h-4 w-4" />
                New
              </Button>
            </div>
            <div className="p-3">
              {!rolesLoading && !rolesError && roles.length > 0 && (
                <div className="relative mb-3">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    value={roleSearch}
                    onChange={(e) => setRoleSearch(e.target.value)}
                    placeholder="Search roles…"
                    className="h-9 pl-9 text-sm"
                  />
                  {roleSearch && (
                    <button
                      type="button"
                      onClick={() => setRoleSearch("")}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label="Clear search"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              )}
              {rolesLoading ? (
                <div className="space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-12 w-full rounded-lg" />
                  ))}
                </div>
              ) : rolesError ? (
                <div className="flex items-center gap-2 rounded-lg border border-destructive/20 bg-destructive/10 p-3 text-xs text-destructive">
                  <AlertTriangle className="h-4 w-4 flex-shrink-0" />
                  {rolesError}
                </div>
              ) : roles.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-10 text-center">
                  <Shield className="h-8 w-8 text-muted-foreground/40" />
                  <p className="text-sm text-muted-foreground">No roles yet.</p>
                  <Button size="sm" variant="outline" onClick={openCreateRole}>
                    <Plus className="mr-1 h-4 w-4" />
                    Create a role
                  </Button>
                </div>
              ) : filteredRoles.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  No roles match your search.
                </p>
              ) : (
                <ul className="space-y-1">
                  {filteredRoles.map((role) => {
                    const isActive = selectedRoleId === role.role_id;
                    return (
                      <li key={role.role_id}>
                        <div
                          className={`group relative flex items-center gap-1 rounded-lg pl-3 pr-1 transition-colors ${
                            isActive
                              ? "bg-primary/10"
                              : "hover:bg-accent"
                          }`}
                        >
                          {isActive && (
                            <span className="absolute inset-y-2 left-0 w-0.5 rounded-full bg-primary" />
                          )}
                          <button
                            type="button"
                            className="min-w-0 flex-1 py-2 text-left"
                            onClick={() => setSelectedRoleId(role.role_id)}
                          >
                            <div
                              className={`truncate text-sm font-medium ${
                                isActive ? "text-primary" : "text-foreground"
                              }`}
                            >
                              {role.name}
                            </div>
                            {role.description && (
                              <div className="truncate text-xs text-muted-foreground">
                                {role.description}
                              </div>
                            )}
                          </button>
                          <button
                            type="button"
                            className="rounded p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
                            onClick={() => openEditRole(role)}
                            aria-label={`Edit ${role.name}`}
                          >
                            <Pencil className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            className="rounded p-1.5 text-muted-foreground opacity-0 transition-opacity hover:bg-background hover:text-destructive focus-visible:opacity-100 group-hover:opacity-100"
                            onClick={() => setDeleteRoleTarget(role)}
                            aria-label={`Delete ${role.name}`}
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>

          {/* Detail pane (right) */}
          <div className="rounded-xl border border-border bg-card">
            {!selectedRole ? (
              <div className="flex flex-col items-center justify-center gap-3 px-6 py-20 text-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                  <ShieldCheck className="h-6 w-6 text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">
                  Select a role to manage its permissions and users.
                </p>
              </div>
            ) : (
              <>
                <div className="border-b border-border px-5 py-4">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <h2 className="text-lg font-semibold text-foreground">{selectedRole.name}</h2>
                      {selectedRole.description ? (
                        <p className="mt-0.5 text-sm text-muted-foreground">
                          {selectedRole.description}
                        </p>
                      ) : (
                        <p className="mt-0.5 text-sm italic text-muted-foreground/70">
                          No description
                        </p>
                      )}
                    </div>
                    <Button size="sm" variant="outline" onClick={() => openEditRole(selectedRole)}>
                      <Pencil className="mr-2 h-3.5 w-3.5" />
                      Edit
                    </Button>
                  </div>
                </div>

                <div className="flex gap-1 border-b border-border px-3">
                  {(
                    [
                      { key: "permissions", label: "Permissions", icon: KeyRound, count: assignedCount },
                      { key: "users", label: "Users", icon: Users, count: roleUsersTotal },
                    ] as { key: TDetailTab; label: string; icon: typeof KeyRound; count: number }[]
                  ).map(({ key, label, icon: Icon, count }) => (
                    <button
                      key={key}
                      onClick={() => setTab(key)}
                      className={`-mb-px flex items-center gap-2 border-b-2 px-3 py-2.5 text-sm font-medium transition-colors ${
                        tab === key
                          ? "border-primary text-primary"
                          : "border-transparent text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                      {label}
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${
                          tab === key
                            ? "bg-primary/10 text-primary"
                            : "bg-muted text-muted-foreground"
                        }`}
                      >
                        {count}
                      </span>
                    </button>
                  ))}
                </div>

                <div className="p-5">
                {tab === "permissions" &&
                  (permsLoading ? (
                    <div className="space-y-2">
                      {Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-14 w-full rounded-lg" />
                      ))}
                    </div>
                  ) : allPermissions.length === 0 ? (
                    <p className="py-6 text-center text-sm text-muted-foreground">
                      No permissions defined.
                    </p>
                  ) : (
                    <>
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <div className="relative min-w-[12rem] flex-1">
                        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          placeholder="Search permissions…"
                          value={permSearch}
                          onChange={(e) => setPermSearch(e.target.value)}
                          className="pl-9"
                        />
                      </div>
                      <span className="whitespace-nowrap text-xs text-muted-foreground">
                        {assignedCount} of {allPermissions.length} assigned
                      </span>
                    </div>
                    <ul className="space-y-1.5">
                      {pagedPermissions.map((perm) => {
                        const isAssigned = assignedIds.has(perm.permission_id);
                        return (
                          <li
                            key={perm.permission_id}
                            className={`flex items-center gap-3 rounded-lg border px-3 py-2.5 text-sm transition-colors ${
                              isAssigned
                                ? "border-primary/30 bg-primary/5"
                                : "border-border bg-background"
                            }`}
                          >
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium text-foreground">{perm.name}</div>
                              <div className="mt-1 flex flex-wrap items-center gap-1.5">
                                <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                                  {perm.resource}
                                </span>
                                <span className="rounded bg-muted px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground">
                                  {perm.action}
                                </span>
                              </div>
                            </div>
                            <Button
                              variant={isAssigned ? "outline" : "default"}
                              size="sm"
                              disabled={permMutating === perm.permission_id}
                              onClick={() => void togglePermission(perm)}
                            >
                              {permMutating === perm.permission_id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : isAssigned ? (
                                <>
                                  <X className="mr-1 h-4 w-4" />
                                  Remove
                                </>
                              ) : (
                                <>
                                  <Plus className="mr-1 h-4 w-4" />
                                  Add
                                </>
                              )}
                            </Button>
                          </li>
                        );
                      })}
                    </ul>
                    {permTotalPages > 1 && (
                      <div className="flex items-center justify-between gap-4 pt-4">
                        <p className="text-xs text-muted-foreground">
                          Page {permPage} of {permTotalPages} ({filteredPermissions.length} shown)
                        </p>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={permPage <= 1}
                            onClick={() => setPermPage((p) => Math.max(1, p - 1))}
                          >
                            <ChevronLeft className="h-4 w-4" />
                            Prev
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={permPage >= permTotalPages}
                            onClick={() => setPermPage((p) => Math.min(permTotalPages, p + 1))}
                          >
                            Next
                            <ChevronRight className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    )}
                    </>
                  ))}

                {tab === "users" && (
                  <div className="space-y-4">
                    <div className="rounded-lg border border-dashed border-border bg-muted/30 p-3">
                      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">
                        Assign this role to a user
                      </label>
                      <div className="flex flex-wrap items-center gap-2">
                        <div className="min-w-[14rem] flex-1">
                          <UserPicker value={assignUser} onChange={setAssignUser} />
                        </div>
                        <Button
                          onClick={() => void handleAssignUser()}
                          disabled={userMutating === "assign" || !assignUser}
                        >
                          {userMutating === "assign" ? (
                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                          ) : (
                            <UserPlus className="mr-2 h-4 w-4" />
                          )}
                          Add user
                        </Button>
                      </div>
                    </div>

                    {usersLoading ? (
                      <div className="space-y-2">
                        {Array.from({ length: 4 }).map((_, i) => (
                          <Skeleton key={i} className="h-14 w-full rounded-lg" />
                        ))}
                      </div>
                    ) : roleUsers.length === 0 ? (
                      <div className="flex flex-col items-center gap-2 py-10 text-center">
                        <Users className="h-8 w-8 text-muted-foreground/40" />
                        <p className="text-sm text-muted-foreground">No users have this role.</p>
                      </div>
                    ) : (
                      <ul className="space-y-1.5">
                        {roleUsers.map((u) => (
                          <li
                            key={u.id}
                            className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2.5 text-sm"
                          >
                            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">
                              {userInitials(u)}
                            </span>
                            <div className="min-w-0 flex-1">
                              <div className="truncate font-medium text-foreground">{userName(u)}</div>
                              {u.email && (
                                <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                              )}
                            </div>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={userMutating === u.id}
                              onClick={() => void handleRemoveUser(u)}
                            >
                              {userMutating === u.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <UserMinus className="mr-2 h-4 w-4" />
                                  Remove
                                </>
                              )}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    )}

                    {roleUsersTotal > 0 && (
                      <div className="flex items-center justify-between gap-4 pt-1">
                        <p className="text-xs text-muted-foreground">
                          Showing {roleUsersOffset + 1}–
                          {Math.min(roleUsersOffset + USERS_PAGE_SIZE, roleUsersTotal)} of {roleUsersTotal}
                        </p>
                        {usersTotalPages > 1 && (
                          <div className="flex gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={usersCurrentPage <= 1 || usersLoading}
                              onClick={() => goToUsersPage(roleUsersOffset - USERS_PAGE_SIZE)}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Prev
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              disabled={usersCurrentPage >= usersTotalPages || usersLoading}
                              onClick={() => goToUsersPage(roleUsersOffset + USERS_PAGE_SIZE)}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Role create/edit */}
      <Dialog
        open={roleEditor.open}
        onOpenChange={(open) => {
          if (!open) setRoleEditor(EMPTY_ROLE_EDITOR);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{roleEditor.role ? "Edit Role" : "New Role"}</DialogTitle>
            <DialogDescription>Roles group permissions granted to users.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">Name</label>
              <Input
                value={roleEditor.name}
                onChange={(e) => setRoleEditor((s) => ({ ...s, name: e.target.value }))}
                placeholder="Role name"
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-muted-foreground">
                Description
              </label>
              <Textarea
                value={roleEditor.description}
                onChange={(e) => setRoleEditor((s) => ({ ...s, description: e.target.value }))}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRoleEditor(EMPTY_ROLE_EDITOR)}
              disabled={isSavingRole}
            >
              Cancel
            </Button>
            <Button onClick={() => void handleSaveRole()} disabled={isSavingRole}>
              {isSavingRole ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
              ) : (
                "Save"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete role confirm */}
      <Dialog
        open={!!deleteRoleTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteRoleTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Role</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete the role “{deleteRoleTarget?.name}”? This cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteRoleTarget(null)}
              disabled={isDeletingRole}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => void handleDeleteRole()}
              disabled={isDeletingRole}
            >
              {isDeletingRole ? (
                <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</>
              ) : (
                "Delete"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
